/**
 * TabMetricas — Fase 1: Métricas del cliente.
 * 
 * - Ingreso manual de métricas por plataforma (Modal)
 * - Parseo de CSV con PapaParse
 * - Sincronización en vivo con Meta / Instagram Graph API
 * - Botón "Analizar con IA" (Gemini)
 * - Generador y exportador de Reporte PDF
 * - Gráficos de evolución (Recharts)
 */

import { useEffect, useState } from 'react';
import {
  BarChart3,
  Upload,
  Sparkles,
  FileText,
  Loader2,
  Plus,
  Instagram,
  CheckCircle2,
  AlertCircle,
  Printer,
  X,
  Edit2,
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { api } from '@/lib/api';
import type { Metric } from '@/types';

interface Props {
  clientId: string;
  clientName: string;
  period: string;
}

const PLATFORM_OPTIONS = [
  'Instagram',
  'Facebook',
  'TikTok',
  'LinkedIn',
  'YouTube',
  'Twitter / X',
  'Sitio Web',
  'Google Ads',
];

export function TabMetricas({ clientId, clientName, period }: Props) {
  const [metrics, setMetrics] = useState<Metric[]>([]);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [analysis, setAnalysis] = useState<string | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [syncNotice, setSyncNotice] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [availableAccounts, setAvailableAccounts] = useState<Array<{ page_name: string; instagram_id: string; username: string }> | null>(null);

  // Modal para agregar / editar métrica manual
  const [metricModalOpen, setMetricModalOpen] = useState(false);
  const [editingMetric, setEditingMetric] = useState<Metric | null>(null);
  const [metricForm, setMetricForm] = useState({
    platform: 'Instagram',
    followers: '0',
    reach: '0',
    impressions: '0',
    engagement_rate: '0',
    clicks: '0',
    likes: '0',
    comments: '0',
    saves: '0',
    shares: '0',
    leads: '0',
    conversions: '0',
  });
  const [submittingMetric, setSubmittingMetric] = useState(false);

  const loadMetrics = () => {
    api
      .getMetrics(clientId, { period })
      .then((res) => {
        const m = res.metrics as Metric[];
        setMetrics(m);
        const withAnalysis = m.find((metric) => metric.ai_analysis);
        if (withAnalysis) setAnalysis(withAnalysis.ai_analysis);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadMetrics();
  }, [clientId, period]);

  const handleSyncMeta = async (chosenAccountId?: string) => {
    setSyncing(true);
    setSyncNotice(null);
    setAvailableAccounts(null);
    try {
      const res = await api.syncMetaMetrics(clientId, {
        period,
        instagram_account_id: chosenAccountId,
      });
      setSyncNotice({ type: 'success', text: res.message || 'Métricas sincronizadas con éxito desde Meta.' });
      loadMetrics();
      if (res.metrics?.ai_analysis) {
        setAnalysis(res.metrics.ai_analysis);
      }
    } catch (err: any) {
      console.error('Error sincronizando con Meta:', err);
      if (err.available_accounts && err.available_accounts.length > 0) {
        setAvailableAccounts(err.available_accounts);
      } else {
        setSyncNotice({
          type: 'error',
          text: err.message || 'No se pudieron sincronizar las métricas. Conecta tu cuenta de Meta primero.',
        });
      }
    } finally {
      setSyncing(false);
    }
  };

  const handleAnalyzeWithAI = async () => {
    if (metrics.length === 0) return;
    setAnalyzing(true);
    try {
      const metricsData = metrics.map((m) => ({
        clientName,
        period: m.period,
        platform: m.platform,
        followers: m.followers,
        reach: m.reach,
        impressions: m.impressions,
        engagement_rate: m.engagement_rate,
        clicks: m.clicks,
        saves: m.saves,
        shares: m.shares,
        comments: m.comments,
        likes: m.likes,
        leads: m.leads,
        conversions: m.conversions,
      }));

      const res = await api.analyzeMetrics(metricsData);
      setAnalysis(res.analysis);
    } catch (error) {
      console.error('Error analizando:', error);
    } finally {
      setAnalyzing(false);
    }
  };

  const handleCSVUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const Papa = (await import('papaparse')).default;

    Papa.parse(file, {
      header: true,
      complete: async (results) => {
        try {
          const metricsData = results.data.map((row: any) => ({
            client_id: clientId,
            period,
            platform: row.platform || row.plataforma || 'instagram',
            followers: parseInt(row.followers || row.seguidores || '0'),
            reach: parseInt(row.reach || row.alcance || '0'),
            impressions: parseInt(row.impressions || row.impresiones || '0'),
            engagement_rate: parseFloat(row.engagement_rate || row.engagement || '0'),
            clicks: parseInt(row.clicks || row.clics || '0'),
            saves: parseInt(row.saves || row.guardados || '0'),
            shares: parseInt(row.shares || row.compartidos || '0'),
            comments: parseInt(row.comments || row.comentarios || '0'),
            likes: parseInt(row.likes || row.me_gusta || '0'),
            leads: parseInt(row.leads || '0'),
            conversions: parseInt(row.conversions || row.conversiones || '0'),
          }));

          await api.importMetricsBatch(metricsData);
          loadMetrics();
        } catch (error) {
          console.error('Error importando CSV:', error);
        }
      },
    });
  };

  const openNewMetricModal = () => {
    setEditingMetric(null);
    setMetricForm({
      platform: 'Instagram',
      followers: '0',
      reach: '0',
      impressions: '0',
      engagement_rate: '0',
      clicks: '0',
      likes: '0',
      comments: '0',
      saves: '0',
      shares: '0',
      leads: '0',
      conversions: '0',
    });
    setMetricModalOpen(true);
  };

  const openEditMetricModal = (m: Metric) => {
    setEditingMetric(m);
    setMetricForm({
      platform: m.platform,
      followers: String(m.followers || 0),
      reach: String(m.reach || 0),
      impressions: String(m.impressions || 0),
      engagement_rate: String(m.engagement_rate || 0),
      clicks: String(m.clicks || 0),
      likes: String(m.likes || 0),
      comments: String(m.comments || 0),
      saves: String(m.saves || 0),
      shares: String(m.shares || 0),
      leads: String(m.leads || 0),
      conversions: String(m.conversions || 0),
    });
    setMetricModalOpen(true);
  };

  const handleSaveMetric = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmittingMetric(true);
    try {
      await api.saveMetrics({
        client_id: clientId,
        period,
        platform: metricForm.platform,
        followers: parseInt(metricForm.followers || '0', 10),
        reach: parseInt(metricForm.reach || '0', 10),
        impressions: parseInt(metricForm.impressions || '0', 10),
        engagement_rate: parseFloat(metricForm.engagement_rate || '0'),
        clicks: parseInt(metricForm.clicks || '0', 10),
        likes: parseInt(metricForm.likes || '0', 10),
        comments: parseInt(metricForm.comments || '0', 10),
        saves: parseInt(metricForm.saves || '0', 10),
        shares: parseInt(metricForm.shares || '0', 10),
        leads: parseInt(metricForm.leads || '0', 10),
        conversions: parseInt(metricForm.conversions || '0', 10),
      });

      setMetricModalOpen(false);
      loadMetrics();
    } catch (err) {
      console.error('Error guardando métricas:', err);
      alert('Error guardando métrica');
    } finally {
      setSubmittingMetric(false);
    }
  };

  // Generador de reporte PDF imprimible
  const handleGeneratePDF = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      alert('Por favor habilita las ventanas emergentes (popups) para exportar el reporte PDF.');
      return;
    }

    const totalFollowers = metrics.reduce((acc, m) => acc + (m.followers || 0), 0);
    const totalReach = metrics.reduce((acc, m) => acc + (m.reach || 0), 0);
    const totalImpressions = metrics.reduce((acc, m) => acc + (m.impressions || 0), 0);
    const totalClicks = metrics.reduce((acc, m) => acc + (m.clicks || 0), 0);
    const totalLeads = metrics.reduce((acc, m) => acc + (m.leads || 0), 0);

    const htmlContent = `
      <!DOCTYPE html>
      <html lang="es">
      <head>
        <meta charset="UTF-8">
        <title>Reporte de Métricas - ${clientName} (${period})</title>
        <style>
          body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif; color: #1e293b; line-height: 1.5; padding: 40px; margin: 0; background: #fff; }
          .header { border-bottom: 2px solid #6366f1; padding-bottom: 20px; margin-bottom: 30px; display: flex; justify-content: space-between; align-items: flex-end; }
          .header h1 { font-size: 26px; margin: 0; color: #0f172a; }
          .header .subtitle { color: #64748b; font-size: 14px; margin-top: 4px; }
          .badge { background: #eef2ff; color: #4f46e5; font-weight: bold; padding: 4px 10px; border-radius: 6px; font-size: 13px; }
          .kpis { display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px; margin-bottom: 30px; }
          .kpi-card { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 10px; padding: 14px; }
          .kpi-card .label { font-size: 11px; text-transform: uppercase; color: #64748b; font-weight: bold; }
          .kpi-card .val { font-size: 22px; font-weight: bold; color: #0f172a; margin-top: 4px; }
          table { width: 100%; border-collapse: collapse; margin-bottom: 30px; }
          th { background: #f1f5f9; text-align: left; padding: 10px; font-size: 12px; color: #475569; border-bottom: 1px solid #cbd5e1; }
          td { padding: 10px; font-size: 13px; border-bottom: 1px solid #e2e8f0; }
          .text-right { text-align: right; }
          .ai-box { background: #faf5ff; border: 1px solid #e9d5ff; border-radius: 10px; padding: 20px; margin-top: 30px; }
          .ai-box h3 { color: #6b21a8; font-size: 16px; margin-top: 0; margin-bottom: 10px; }
          .ai-box pre { white-space: pre-wrap; font-family: inherit; font-size: 13px; color: #374151; margin: 0; }
          .footer { margin-top: 50px; font-size: 11px; color: #94a3b8; text-align: center; border-top: 1px solid #f1f5f9; padding-top: 15px; }
          @media print {
            body { padding: 20px; }
            button { display: none; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <div>
            <h1>Reporte de Rendimiento Digital</h1>
            <div class="subtitle">Cliente: <strong>${clientName}</strong> — Período: <strong>${period}</strong></div>
          </div>
          <div class="badge">Teresita Marketing SaaS</div>
        </div>

        <div class="kpis">
          <div class="kpi-card">
            <div class="label">Seguidores Totales</div>
            <div class="val">${totalFollowers.toLocaleString()}</div>
          </div>
          <div class="kpi-card">
            <div class="label">Alcance Total</div>
            <div class="val">${totalReach.toLocaleString()}</div>
          </div>
          <div class="kpi-card">
            <div class="label">Impresiones</div>
            <div class="val">${totalImpressions.toLocaleString()}</div>
          </div>
          <div class="kpi-card">
            <div class="label">Clicks / Leads</div>
            <div class="val">${totalClicks} / ${totalLeads}</div>
          </div>
        </div>

        <h3>Desglose de Plataformas</h3>
        <table>
          <thead>
            <tr>
              <th>Plataforma</th>
              <th class="text-right">Seguidores</th>
              <th class="text-right">Alcance</th>
              <th class="text-right">Impresiones</th>
              <th class="text-right">Engagement</th>
              <th class="text-right">Clicks</th>
              <th class="text-right">Likes</th>
            </tr>
          </thead>
          <tbody>
            ${metrics
              .map(
                (m) => `
              <tr>
                <td><strong>${m.platform}</strong></td>
                <td class="text-right">${m.followers.toLocaleString()}</td>
                <td class="text-right">${m.reach.toLocaleString()}</td>
                <td class="text-right">${m.impressions.toLocaleString()}</td>
                <td class="text-right">${m.engagement_rate}%</td>
                <td class="text-right">${m.clicks}</td>
                <td class="text-right">${m.likes}</td>
              </tr>
            `
              )
              .join('')}
          </tbody>
        </table>

        ${
          analysis
            ? `
          <div class="ai-box">
            <h3>✨ Diagnóstico y Recomendaciones Estratégicas (IA)</h3>
            <pre>${analysis}</pre>
          </div>
        `
            : ''
        }

        <div class="footer">
          Generado automáticamente el ${new Date().toLocaleDateString('es-AR')} con 💜 por Teresita — SaaS de Gestión de Marketing.
        </div>

        <script>
          window.onload = function() {
            setTimeout(function() { window.print(); }, 400);
          };
        </script>
      </body>
      </html>
    `;

    printWindow.document.open();
    printWindow.document.write(htmlContent);
    printWindow.document.close();
  };

  // Chart data
  const chartData = metrics.map((m) => ({
    platform: m.platform,
    Alcance: m.reach,
    Impresiones: m.impressions,
    Clicks: m.clicks,
    Likes: m.likes,
  }));

  if (loading) {
    return (
      <div className="flex items-center justify-center h-32">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* ─── Acciones ─── */}
      <div className="flex flex-wrap items-center gap-3">
        {/* Sincronización en vivo con Meta */}
        <Button
          onClick={() => handleSyncMeta()}
          disabled={syncing}
          className="gap-2 bg-gradient-to-r from-pink-600 via-rose-600 to-indigo-600 hover:from-pink-700 hover:to-indigo-700 text-white shadow-sm"
        >
          {syncing ? (
            <Loader2 size={16} className="animate-spin" />
          ) : (
            <Instagram size={16} />
          )}
          {syncing ? 'Sincronizando...' : 'Sincronizar con Meta'}
        </Button>

        <Button variant="outline" className="gap-2" asChild>
          <label className="cursor-pointer">
            <Upload size={16} />
            Importar CSV
            <input
              type="file"
              accept=".csv"
              className="hidden"
              onChange={handleCSVUpload}
            />
          </label>
        </Button>

        <Button
          variant="outline"
          className="gap-2"
          onClick={handleAnalyzeWithAI}
          disabled={analyzing || metrics.length === 0}
        >
          {analyzing ? (
            <Loader2 size={16} className="animate-spin" />
          ) : (
            <Sparkles size={16} />
          )}
          Analizar con IA
        </Button>

        <Button
          variant="outline"
          className="gap-2"
          onClick={handleGeneratePDF}
          disabled={metrics.length === 0}
          title={metrics.length === 0 ? 'Registra métricas para exportar el reporte' : 'Exportar reporte ejecutivo en PDF'}
        >
          <Printer size={16} />
          Generar PDF / Imprimir
        </Button>
      </div>

      {/* Notificación de sincronización */}
      {syncNotice && (
        <div
          className={`flex items-center gap-2 p-3 rounded-xl text-xs font-medium ${
            syncNotice.type === 'success'
              ? 'bg-emerald-50 text-emerald-800 border border-emerald-200'
              : 'bg-rose-50 text-rose-800 border border-rose-200'
          }`}
        >
          {syncNotice.type === 'success' ? (
            <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
          ) : (
            <AlertCircle className="w-4 h-4 text-rose-600 shrink-0" />
          )}
          <span>{syncNotice.text}</span>
        </div>
      )}

      {/* Selector de cuenta de Instagram si hay varias disponibles */}
      {availableAccounts && availableAccounts.length > 0 && (
        <div className="p-4 bg-sky-50 border border-sky-200 rounded-xl space-y-3">
          <div>
            <h4 className="text-sm font-bold text-sky-900">
              Selecciona la cuenta de Instagram de {clientName}
            </h4>
            <p className="text-xs text-sky-700 mt-0.5">
              Encontramos varias cuentas en tu Meta Business Suite. Elige cuál corresponde a este cliente:
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {availableAccounts.map((acc) => (
              <button
                key={acc.instagram_id}
                onClick={() => handleSyncMeta(acc.instagram_id)}
                disabled={syncing}
                className="flex items-center justify-between p-2.5 rounded-lg bg-white border border-sky-200 hover:border-pink-500 hover:shadow-sm text-left transition-all"
              >
                <div>
                  <p className="text-xs font-semibold text-slate-800">{acc.page_name}</p>
                  <p className="text-[11px] text-pink-600 font-medium">@{acc.username}</p>
                </div>
                <Instagram className="w-4 h-4 text-pink-500 shrink-0" />
              </button>
            ))}
          </div>
        </div>
      )}

      {/* ─── Gráfico ─── */}
      {chartData.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Métricas del Período</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="platform" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'hsl(var(--card))',
                    border: '1px solid hsl(var(--border))',
                    borderRadius: '8px',
                  }}
                />
                <Bar dataKey="Alcance" fill="hsl(262 83% 58%)" radius={[4, 4, 0, 0]} />
                <Bar dataKey="Impresiones" fill="hsl(262 83% 68%)" radius={[4, 4, 0, 0]} />
                <Bar dataKey="Clicks" fill="hsl(262 83% 78%)" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* ─── Tabla de Métricas ─── */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center justify-between">
            <span className="flex items-center gap-2">
              <BarChart3 size={16} />
              Datos por Plataforma ({metrics.length})
            </span>
            <Button size="sm" onClick={openNewMetricModal} className="gap-1 shadow-xs text-xs">
              <Plus size={14} />
              Agregar Métrica
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {metrics.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-sm text-muted-foreground">
                No hay métricas registradas para este período.
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                Sincroniza con Meta, importa un CSV o agrega datos manualmente.
              </p>
              <Button size="sm" variant="outline" onClick={openNewMetricModal} className="mt-3 gap-1 text-xs">
                <Plus size={13} /> Agregar datos manualmente
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-slate-500 text-xs">
                    <th className="pb-2 font-medium">Plataforma</th>
                    <th className="pb-2 font-medium text-right">Seguidores</th>
                    <th className="pb-2 font-medium text-right">Alcance</th>
                    <th className="pb-2 font-medium text-right">Engagement</th>
                    <th className="pb-2 font-medium text-right">Clicks</th>
                    <th className="pb-2 font-medium text-right">Leads</th>
                    <th className="pb-2 font-medium text-right">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {metrics.map((m) => (
                    <tr key={m.id} className="border-b last:border-0 hover:bg-slate-50/50">
                      <td className="py-2.5">
                        <Badge variant="outline" className="font-semibold">{m.platform}</Badge>
                      </td>
                      <td className="py-2.5 text-right font-mono font-medium">
                        {m.followers?.toLocaleString()}
                      </td>
                      <td className="py-2.5 text-right font-mono font-medium">
                        {m.reach?.toLocaleString()}
                      </td>
                      <td className="py-2.5 text-right font-mono font-medium">
                        {m.engagement_rate}%
                      </td>
                      <td className="py-2.5 text-right font-mono">{m.clicks}</td>
                      <td className="py-2.5 text-right font-mono">{m.leads}</td>
                      <td className="py-2.5 text-right">
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => openEditMetricModal(m)}
                          className="h-7 w-7 text-slate-400 hover:text-slate-700"
                          title="Editar métrica"
                        >
                          <Edit2 size={13} />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ─── Análisis IA ─── */}
      {analysis && (
        <Card className="border-primary/30 shadow-xs">
          <CardHeader>
            <CardTitle className="text-base flex items-center justify-between">
              <span className="flex items-center gap-2">
                <Sparkles size={16} className="text-primary" />
                Diagnóstico Estratégico de IA (Gemini)
              </span>
              <Button size="sm" variant="outline" onClick={handleGeneratePDF} className="h-7 text-xs gap-1">
                <FileText size={12} /> Incluir en reporte PDF
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="prose prose-sm max-w-none text-slate-800 bg-slate-50 p-4 rounded-xl border border-slate-200">
              <pre className="whitespace-pre-wrap text-sm font-sans leading-relaxed">{analysis}</pre>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ─── Modal Agregar / Editar Métrica Manual ─── */}
      {metricModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-xs p-4 animate-in fade-in">
          <div className="bg-white rounded-2xl border border-slate-200 shadow-xl w-full max-w-lg overflow-hidden flex flex-col max-h-[90vh]">
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
              <h3 className="font-semibold text-slate-900 text-sm">
                {editingMetric ? `Editar Métricas: ${editingMetric.platform}` : 'Registrar Métrica Manual'}
              </h3>
              <button
                type="button"
                onClick={() => setMetricModalOpen(false)}
                className="text-slate-400 hover:text-slate-600 p-1"
              >
                <X size={16} />
              </button>
            </div>

            <form onSubmit={handleSaveMetric} className="p-5 space-y-4 overflow-y-auto">
              <div>
                <label className="text-xs font-semibold text-slate-700 block mb-1">
                  Plataforma
                </label>
                <select
                  value={metricForm.platform}
                  onChange={(e) => setMetricForm({ ...metricForm, platform: e.target.value })}
                  className="w-full px-3 py-2 text-xs border border-slate-200 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary bg-white"
                >
                  {PLATFORM_OPTIONS.map((plat) => (
                    <option key={plat} value={plat}>
                      {plat}
                    </option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                <div>
                  <label className="text-[11px] font-semibold text-slate-700 block mb-1">
                    Seguidores
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={metricForm.followers}
                    onChange={(e) => setMetricForm({ ...metricForm, followers: e.target.value })}
                    className="w-full px-2.5 py-1.5 text-xs border border-slate-200 rounded-lg focus:ring-1 focus:ring-primary"
                  />
                </div>

                <div>
                  <label className="text-[11px] font-semibold text-slate-700 block mb-1">
                    Alcance (Reach)
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={metricForm.reach}
                    onChange={(e) => setMetricForm({ ...metricForm, reach: e.target.value })}
                    className="w-full px-2.5 py-1.5 text-xs border border-slate-200 rounded-lg focus:ring-1 focus:ring-primary"
                  />
                </div>

                <div>
                  <label className="text-[11px] font-semibold text-slate-700 block mb-1">
                    Impresiones
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={metricForm.impressions}
                    onChange={(e) => setMetricForm({ ...metricForm, impressions: e.target.value })}
                    className="w-full px-2.5 py-1.5 text-xs border border-slate-200 rounded-lg focus:ring-1 focus:ring-primary"
                  />
                </div>

                <div>
                  <label className="text-[11px] font-semibold text-slate-700 block mb-1">
                    Engagement (%)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={metricForm.engagement_rate}
                    onChange={(e) => setMetricForm({ ...metricForm, engagement_rate: e.target.value })}
                    className="w-full px-2.5 py-1.5 text-xs border border-slate-200 rounded-lg focus:ring-1 focus:ring-primary"
                  />
                </div>

                <div>
                  <label className="text-[11px] font-semibold text-slate-700 block mb-1">
                    Clicks / Enlace
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={metricForm.clicks}
                    onChange={(e) => setMetricForm({ ...metricForm, clicks: e.target.value })}
                    className="w-full px-2.5 py-1.5 text-xs border border-slate-200 rounded-lg focus:ring-1 focus:ring-primary"
                  />
                </div>

                <div>
                  <label className="text-[11px] font-semibold text-slate-700 block mb-1">
                    Me Gusta (Likes)
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={metricForm.likes}
                    onChange={(e) => setMetricForm({ ...metricForm, likes: e.target.value })}
                    className="w-full px-2.5 py-1.5 text-xs border border-slate-200 rounded-lg focus:ring-1 focus:ring-primary"
                  />
                </div>

                <div>
                  <label className="text-[11px] font-semibold text-slate-700 block mb-1">
                    Comentarios
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={metricForm.comments}
                    onChange={(e) => setMetricForm({ ...metricForm, comments: e.target.value })}
                    className="w-full px-2.5 py-1.5 text-xs border border-slate-200 rounded-lg focus:ring-1 focus:ring-primary"
                  />
                </div>

                <div>
                  <label className="text-[11px] font-semibold text-slate-700 block mb-1">
                    Guardados
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={metricForm.saves}
                    onChange={(e) => setMetricForm({ ...metricForm, saves: e.target.value })}
                    className="w-full px-2.5 py-1.5 text-xs border border-slate-200 rounded-lg focus:ring-1 focus:ring-primary"
                  />
                </div>

                <div>
                  <label className="text-[11px] font-semibold text-slate-700 block mb-1">
                    Compartidos
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={metricForm.shares}
                    onChange={(e) => setMetricForm({ ...metricForm, shares: e.target.value })}
                    className="w-full px-2.5 py-1.5 text-xs border border-slate-200 rounded-lg focus:ring-1 focus:ring-primary"
                  />
                </div>

                <div>
                  <label className="text-[11px] font-semibold text-slate-700 block mb-1">
                    Leads / Consultas
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={metricForm.leads}
                    onChange={(e) => setMetricForm({ ...metricForm, leads: e.target.value })}
                    className="w-full px-2.5 py-1.5 text-xs border border-slate-200 rounded-lg focus:ring-1 focus:ring-primary"
                  />
                </div>

                <div>
                  <label className="text-[11px] font-semibold text-slate-700 block mb-1">
                    Conversiones / Ventas
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={metricForm.conversions}
                    onChange={(e) => setMetricForm({ ...metricForm, conversions: e.target.value })}
                    className="w-full px-2.5 py-1.5 text-xs border border-slate-200 rounded-lg focus:ring-1 focus:ring-primary"
                  />
                </div>
              </div>

              <div className="flex items-center justify-end gap-2 pt-3 border-t border-slate-100">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setMetricModalOpen(false)}
                  className="text-xs"
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  size="sm"
                  disabled={submittingMetric}
                  className="text-xs bg-primary hover:bg-primary/90 text-white"
                >
                  {submittingMetric ? <Loader2 size={13} className="animate-spin" /> : 'Guardar Métrica'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
